import { ComponentConfig, type Config } from '@measured/puck';
import {
  ColumnsProps,
  FlexProps,
  ContainerProps,
  VerticalSpaceProps,
  TextFieldProps,
  fontFamilies,
  AccordionProps,
  DividerProps,
  TextEditorProps,
  ButtonSectionProps,
  ImageUploaderProps,
  PuckRenderPage,
  createComponentConfigs,
  ThemeColorComponent,
  ErrorColor,
  CustomCss,
  CustomScripts,
} from 'shared-components';
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks';
import { baseService } from 'services';

type Props = {
  // Typology Components
  TextField: TextFieldProps;
  Columns: ColumnsProps;
  Flex: FlexProps;
  VerticalSpace: VerticalSpaceProps;
  TextEditor: TextEditorProps;
  ImageUploader: ImageUploaderProps;
  Divider: DividerProps;
  Container: ContainerProps;
  Button: ButtonSectionProps;
  Accordion: AccordionProps;
};

export const FreeTemplateconfig = (): Config<Props> => {
  const componentList = createComponentConfigs({
    useAppDispatch,
    useAppSelector,
    type: 'freestyle',
    baseService,
  });
  return {
    categories: {
      Blocks: {
        components: [
          'Columns',
          'Flex',
          'VerticalSpace',
          'Container',
          'Accordion',
          'Divider',
        ],
        defaultExpanded: false,
      },
      Text: {
        components: ['TextField', 'TextEditor'],
        defaultExpanded: false,
      },
      Buttons: {
        components: ['Button'],
        defaultExpanded: false,
      },
      other: {
        defaultExpanded: false,
      },
    },
    components: componentList as {
      [ComponentName in keyof Props]: Omit<
        ComponentConfig<Props[ComponentName], Props[ComponentName]>,
        'type'
      >;
    },
    root: {
      fields: {
        fontFamily: {
          label: 'Font Family',
          type: 'select',
          options: fontFamilies,
        },
        themeColor: {
          type: 'custom',
          label: 'Theme Color',
          render: ({ value, onChange }) => (
            <ThemeColorComponent value={value} onChange={onChange} />
          ),
        },
        errorColor: {
          type: 'custom',
          label: 'Error Color',
          render: ({ value, onChange }) => (
            <ErrorColor value={value} onChange={onChange} />
          ),
        },
        customCss: {
          type: 'custom',
          label: 'Custom CSS',
          render: ({ value, onChange }) => (
            <CustomCss value={value} onChange={onChange} />
          ),
        },
        customScripts: {
          type: 'custom',
          label: 'Custom Scripts',
          render: ({ value, onChange }) => (
            <CustomScripts value={value} onChange={onChange} />
          ),
        },
      },
      defaultProps: {
        fontFamily: fontFamilies[0].value,
        themeColor: { r: 79, g: 70, b: 229 },
        errorColor: '#FF5A50',
        customCss: [],
        customScripts: [],
      },
      render: ({
        children,
        fontFamily,
        errorColor,
        customCss,
        customScripts,
      }) => (
        <PuckRenderPage
          fontFamily={fontFamily}
          errorColor={errorColor}
          customCss={customCss}
          customScripts={customScripts}
          useAppSelector={useAppSelector}>
          {children}
        </PuckRenderPage>
      ),
    },
  };
};

export default FreeTemplateconfig;
